/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2020, Bulb Inc. For more information, please email contact@hellobulb.com.
 *          _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
*/

import {
  AfterViewInit, Component, ElementRef,
  EventEmitter, Input, Output,
} from '@angular/core';
import { Accordion } from 'primeng/accordion';

/**
 * Component for our aero-accordion tab field.
 */
@Component({
  selector: 'aero-accordion-tab',
  templateUrl: './aero-accordion-tab.component.html',
  viewProviders: [Accordion],
  standalone: false,
})
export class AeroAccordionTabComponent implements AfterViewInit {
  /**
   * Whether this tab is disabled or not.
   */
  @Input() disabled : boolean = false;

  /**
   * Add this to your custom component's TS file.
   */
  @Input({ required: true }) panelValue!: string | number;

  /**
   * This guarantees PrimeNG always has a valid string, even if panelValue is empty!.
   */
  public readonly internalId = `panel-${Math.random().toString(36).substring(2, 9)}`;

  /**
   * Two-way binding for the selection (opening) of the tab.
   */
  @Output() selectedChange = new EventEmitter<boolean>();

  @Input()
  get selected() {
    return this.selectedValue;
  }

  set selected(value: boolean) {
    if (this.selectedValue !== value) {
      this.selectedValue = value;
      this.selectedChange.emit(this.selectedValue);
    }
  }

  onAccordionValueChange(event: any) {
    // Check PrimeNG's native state against our unique internal ID
    const isNowOpen = Array.isArray(event)
      ? event.includes(this.internalId)
      : event === this.internalId;
    //
    this.selectedValue = isNowOpen;

    // Emit it to the parent so your icons come back!
    this.selectedChange.emit(this.selectedValue);
  }

  selectedValue : boolean = false;

  /**
   * Whether the tab has focus or not.
   */
  public tabHasFocus : boolean = false;

  /**
   * Default Constructor.
   */
  constructor(private elementRef : ElementRef) {}

  /**
   * A callback method that is invoked immediately after
   * Angular has completed initialization of a component's view.
   * It is invoked only once when the view is instantiated.
   */
  ngAfterViewInit() {
    // Listen for focus/blur events on the header anchor element so we can display focus around the entire
    // tab and not just the header
    const tabAnchor = this.elementRef.nativeElement.querySelector('.p-accordionheader');
    if (tabAnchor) {
      tabAnchor.onfocus = () => { this.tabHasFocus = true; };
      tabAnchor.onblur = () => { this.tabHasFocus = false; };
    }
  }

  /**
   * Event handler to watch for the accordion opening/closing so we can pass that event via our own emitter.
   * @param value Whether the accordion tab is selected/open.
   */
  onSelectedChange(value : boolean) {
    this.selectedValue = value;
    this.selectedChange.emit(this.selectedValue);
  }
}
